/**
 * What is stopping a module, in words.
 *
 * `modulePublishBlockers` returns codes with data behind them, and it does so on
 * purpose: a rule cannot know what language the person reading it speaks, and a
 * lib that reached for the i18n instance would make every test of the rule depend
 * on a locale file. The other half of that decision is here — a code has exactly
 * one sentence, written once, because the two places that show a blocker (the
 * builder, and the dialog that finally publishes) must not describe the same
 * problem two different ways. A blocker with no wording, or with two, is a
 * publish button disabled for a reason the creator cannot read.
 */
import {type MessageDescriptor} from '@lingui/core'
import {msg} from '@lingui/core/macro'

import {MODULE_MIN_CAPSULES, type ModuleBlocker} from '#/lib/modulePlaylist'

/** The `t` of `useLingui()`, as this file needs it. */
type Translate = (descriptor: MessageDescriptor) => string

/**
 * The sentences, one per code.
 *
 * Written as descriptors rather than as strings so they are extracted for
 * translation like every other line of copy, and read here by code rather than by
 * position, so a new blocker cannot quietly inherit the wrong wording.
 */
const SENTENCES = {
  title: msg`Give the module a title.`,
  subject: msg`Choose the subject this module is for.`,
  level: msg`Choose the level this module is for.`,
  duplicate: msg`The same capsule is in the playlist twice.`,
  missing: msg`One of the capsules can no longer be read — replace it.`,
} as const

/** One blocker, as a sentence somebody can act on. */
export function moduleBlockerSentence(
  blocker: ModuleBlocker,
  t: Translate,
): string {
  switch (blocker.code) {
    case 'title':
    case 'subject':
    case 'level':
    case 'duplicate':
    case 'missing':
      return t(SENTENCES[blocker.code])
    case 'too-few':
      return t(
        msg`Add ${blocker.missing} more ${
          blocker.missing === 1 ? 'capsule' : 'capsules'
        } — a module is published with ${blocker.needed}.`,
      )
    case 'unpublished':
      return t(
        msg`“${blocker.title || 'A capsule'}” is still a draft. Publish it to include it.`,
      )
    case 'mismatch':
      return t(
        msg`“${blocker.title || 'A capsule'}” is filed under another subject or level.`,
      )
    default:
      /*
       * Unreachable, and still written down: a blocker this file has not been
       * taught is a disabled button with no explanation, and saying *something*
       * true about it is better than an empty line in a list of jobs.
       */
      return t(
        msg`Something about this module still needs attention before it can be published (a module needs ${MODULE_MIN_CAPSULES} capsules).`,
      )
  }
}
